import { useState, useEffect } from 'react';
import flatten from 'flat';
import { getPhraseTranslations } from '@inter-ikea-storageone/shared/services/phraseTranslationService';
import { getLocale } from '@inter-ikea-storageone/shared/utils/i18nUtils';

interface UseTranslationsReturn {
  translations: Record<string, string> | null;
  locale: string;
}

/**
 * Loads and flattens phrase translations for IntlProvider.
 *
 * Returns null while loading to prevent rendering with missing translations.
 *
 * @returns Object containing translations and locale
 * @returns translations - Flattened translation key-value pairs (null while loading)
 * @returns locale - Current user locale
 *
 * @example
 * ```typescript
 * const { translations, locale } = useTranslations();
 *
 * if (translations === null) return null;
 *
 * return (
 *   <IntlProvider locale={locale} messages={translations}>
 *     <App />
 *   </IntlProvider>
 * );
 * ```
 */
export const useTranslations = (): UseTranslationsReturn => {
  const [translations, setTranslations] = useState<Record<string, string> | null>(null);
  const locale = getLocale();

  useEffect(() => {
    getPhraseTranslations()
      .then(translations => {
        setTranslations(flatten(translations));
      })
      .catch(() => {
        setTranslations({});
      });
  }, []);

  return { translations, locale };
};
